/**
 * authClient — shared session transport (silent-session-expiry-fix spec).
 *
 * Single-flight refresh-on-401 with exactly one retry, for BOTH axios (the
 * default instance — covers dashboard widgets via vite dedupe) and fetch
 * (`authFetch` — used by the generic SDK client and portal overview).
 *
 * Loop-safety invariants (regression-tested — do not weaken):
 *  - requests to /api/v1/auth/* are NEVER intercepted (no refresh loops)
 *  - a request is retried at most ONCE (__authRetried marker)
 *  - 403 is NOT handled here: tenant/RBAC rejection is not session expiry
 *
 * The auth-service ROTATES refresh tokens on every use (single-use): all
 * concurrent 401 handlers MUST await the same refresh (single-flight), and
 * refreshAction MUST store the rotated token — racing refreshes would revoke
 * each other's tokens.
 *
 * Generated by codaic — do not edit outside CODAIC:CUSTOM regions.
 */

import axios from 'axios';
import { refreshAction, tokenStorage } from './authStore';

export const SESSION_EXPIRED_EVENT = 'codaic:session-expired';

let inflight: Promise<boolean> | null = null;
let expiredEmitted = false;

/** Reset after a successful login so a later expiry can notify again. */
export function resetSessionExpired(): void {
  expiredEmitted = false;
}

/** Clear tokens and notify the app shell — at most once per expiry. */
export function emitSessionExpired(): void {
  if (expiredEmitted) return;
  expiredEmitted = true;
  tokenStorage.clear();
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

/**
 * Single-flight refresh: every concurrent caller awaits the SAME
 * refreshAction() call. Resolves true on success, false on failure —
 * never rejects.
 */
export function refreshOnce(): Promise<boolean> {
  if (!inflight) {
    inflight = refreshAction()
      .then(() => {
        expiredEmitted = false;
        return true;
      })
      .catch(() => false)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

function isAuthUrl(url: string | undefined | null): boolean {
  return !!url && url.includes('/api/v1/auth/');
}

let installed = false;

/**
 * Install the refresh-on-401 interceptor on the axios DEFAULT instance
 * (deduped to a single copy per app). Idempotent.
 */
export function installAuthInterceptor(): void {
  if (installed) return;
  installed = true;
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config as (Record<string, unknown> & { url?: string; headers?: Record<string, unknown> }) | undefined;
      const status = error?.response?.status;
      if (status !== 401 || !config || config.__authRetried || isAuthUrl(config.url)) {
        return Promise.reject(error);
      }
      if (!tokenStorage.getRefreshToken()) {
        emitSessionExpired();
        return Promise.reject(error);
      }
      const refreshed = await refreshOnce();
      if (!refreshed) {
        emitSessionExpired();
        return Promise.reject(error);
      }
      config.__authRetried = true;
      config.headers = { ...(config.headers ?? {}), Authorization: `Bearer ${tokenStorage.getAccessToken()}` };
      return axios(config as never);
    },
  );
}

/**
 * fetch() with auth headers + the same 401 semantics as the interceptor:
 * one shared refresh, one retry, session-expired event when unrecoverable.
 */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const doFetch = () => {
    const headers = new Headers(init.headers ?? {});
    const token = tokenStorage.getAccessToken();
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    const tenant = localStorage.getItem('tenant_id');
    if (tenant && !headers.has('X-Tenant-ID')) headers.set('X-Tenant-ID', tenant);
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    return fetch(input, { ...init, headers });
  };

  let res = await doFetch();
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
  if (res.status === 401 && !isAuthUrl(url)) {
    if (tokenStorage.getRefreshToken() && (await refreshOnce())) {
      res = await doFetch();
      if (res.status !== 401) return res;
    }
    emitSessionExpired();
  }
  return res;
}
